import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-ai-content-index',
  standalone: true,
  imports: [
    CommonModule,
    NgxChartsModule,
    MatTooltipModule
  ],
  templateUrl: './ai-content-index.component.html',
  styleUrl: './ai-content-index.component.scss'
})
export class AiContentIndexComponent implements OnInit, OnChanges {
  @Input() tabData: any = null;
  @Input() config: any = null;
  @Input() evidenceSources: any[] = [];
  @Input() recommendations: any[] = [];

  // Chart data
  radarChartData: any[] = [];
  barChartData: any[] = [];
  overallScore: number = 0;
  categoryScores: any = {};
  strengths: string[] = [];
  weaknesses: string[] = [];
  evidence: any[] = [];

  // Chart options
  view: any = [800, 400]; // Default size, will be updated in ngOnInit
  colorScheme: any = {
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA', '#FF7F0E', '#1F77B4']
  };

  chartView: [number, number] = [500, 500]; // default

  constructor() {
    this.setChartView(window.innerWidth);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setChartView(event.target.innerWidth);
  }

  setChartView(width: number) {
    if (width < 576) {
      this.chartView = [250, 250]; // small phones
    } else if (width < 768) {
      this.chartView = [700, 500]; // large phones / small tablets
    } else if (width < 1024) {
      this.chartView = [500, 400]; // tablets
    } else {
      this.chartView = [700, 500]; // desktops
    }
  }




  // constructor() { }

  ngOnInit(): void {
    // Update view size for client-side only
    if (typeof window !== 'undefined') {
      this.view = [window.innerWidth * 0.8, window.innerHeight * 0.6];
    }

    // Initialize chart data arrays
    this.barChartData = [];
    this.radarChartData = [];

    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabData']) {
      if (this.tabData) {
        this.processData();
      } else {
        // Reset data when tabData is null/undefined
        this.barChartData = [];
        this.radarChartData = [];
        this.overallScore = 0;
        this.categoryScores = {};
        this.strengths = [];
        this.weaknesses = [];
        this.evidence = [];
      }
    }
  }

  processData(): void {
    if (!this.tabData) return;

    // Extract AI Content Index data from the new structure
    const universities = this.tabData.universities || [];
    const chartData = this.tabData.chart_data || {};
    const contentSummary = this.tabData.content_summary || {};

    // Use the first university for overall score and analysis
    if (universities.length > 0) {
      const firstUniversity = universities[0];
      this.overallScore = firstUniversity.overall_content_score || 0;
      this.strengths = firstUniversity.content_strengths || [];
      this.weaknesses = firstUniversity.content_weaknesses || [];

      // Extract category scores from content domains
      this.categoryScores = {};
      if (firstUniversity.content_domains) {
        firstUniversity.content_domains.forEach((domain: any) => {
          this.categoryScores[domain.domain] = domain.influence_score || 0;
        });
      }
    }

    // Use recommendations from tabData
    this.recommendations = this.tabData.recommendations || [];

    // Prepare radar chart data
    this.prepareRadarChartData();

    // Prepare bar chart data
    this.prepareBarChartData();

    // Extract evidence from tabData
    this.extractEvidence();
  }

  prepareRadarChartData(): void {
    if (!this.categoryScores || Object.keys(this.categoryScores).length === 0) {
      this.radarChartData = [];
      return;
    }

    // Polar chart expects a different structure with series
    const series = Object.entries(this.categoryScores).map(([category, score]) => ({
      name: this.formatCategoryName(category),
      value: score as number
    }));

    // Only create the chart data if we have valid series
    if (series.length > 0) {
      this.radarChartData = [{
        name: 'Content Analysis',
        series: series
      }];
    } else {
      this.radarChartData = [];
    }
  }

  prepareBarChartData(): void {
    if (!this.tabData || !this.tabData.universities || this.tabData.universities.length === 0) {
      this.barChartData = [];
      return;
    }

    // Limit to 5 universities
    const universities = this.tabData.universities.slice(0, 5);

    // Collect all unique content categories
    const allCategories = new Set<string>();
    universities.forEach((uni: any) => {
      if (uni.content_domains) {
        uni.content_domains.forEach((domain: any) => {
          allCategories.add(domain.domain);
        });
      }
    });

    // Build grouped data structure
    this.barChartData = Array.from(allCategories).map((category: string) => ({
      name: this.formatCategoryName(category),
      series: universities.map((uni: any) => {
        const domain = (uni.content_domains || []).find((d: any) => d.domain === category);
        return {
          name: uni.name || 'University',
          value: domain ? domain.influence_score || 0 : 0
        };
      })
    }));
  }

  extractEvidence(): void {
    this.evidence = [];

    // Use evidence sources from tabData
    if (this.tabData && this.tabData.evidence_sources) {
      this.tabData.evidence_sources.forEach((source: any) => {
        this.evidence.push({
          category: source.category || 'General',
          url: source.url,
          title: source.title,
          credibility: source.confidence * 10, // Convert 0-1 to 0-10 scale
          snippet: source.snippet,
          type: source.source
        });
      });
    }
  }

  formatCategoryName(category: string): string {
    return category
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#5AA454'; // Green
    if (score >= 60) return '#FF7F0E'; // Orange
    return '#A10A28'; // Red
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    return 'score-poor';
  }

  onChartSelect(event: any): void {
    // console.log('Chart selected:', event);
  }

  openEvidenceLink(url: string): void {
    if (url && url.trim() !== '' && url !== '#') {
      // Open the link in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Show a message if no URL is available
      // console.log('No source URL available for this item');
    }
  }

  getEvidenceTooltip(item: any): string {
    if (!item) return '';

    let tooltip = '';
    if (item.category) tooltip += `Category: ${item.category}\n`;
    if (item.credibility && typeof item.credibility === 'number') {
      tooltip += `Credibility: ${item.credibility}/10\n`;
    }
    if (item.snippet) tooltip += `Snippet: ${item.snippet}\n`;
    if (item.type) tooltip += `Type: ${item.type}\n`;
    if (item.url) tooltip += `\nClick to view source`;

    return tooltip.trim();
  }

  getCredibilityColor(score: number): string {
    if (score >= 8) return '#5AA454';
    if (score >= 6) return '#FF7F0E';
    return '#A10A28';
  }

  trackByEvidence(index: number, item: any): string {
    return item.url || index;
  }
}
